/* Helpers de presentación compartidos por las pantallas de Agendamientos (TD-11766). */
import {
  Categoria,
  EstadoReserva,
  EstadoTema,
  MiReserva,
  ModalidadAgenda,
  ReservaGestion,
  TemaDetalle,
  TipoPublico,
} from "src/services/agenda";
import { EventoCalendario } from "src/utils/calendario";
import { fechaDeTextoSql } from "src/utils/fechas";

/** 'YYYY-MM-DD HH:MM' -> 'miércoles 9 de octubre, 14:30' */
export const formatFechaLarga = (valor?: string | null) => {
  const d = fechaDeTextoSql(valor);
  if (!d) return valor || "Sin fecha";
  return d.toLocaleString("es-PY", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
};

/** 'YYYY-MM-DD HH:MM' -> 'HH:MM' */
export const hora = (valor?: string | null) => (valor ? valor.slice(11, 16) : "");

/** 'YYYY-MM-DD' -> 'miércoles 9/10' */
export const formatDia = (ymd: string) => {
  const d = new Date(`${ymd}T00:00:00`);
  if (isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("es-PY", { weekday: "long", day: "numeric", month: "numeric" });
};

/** Fecha local 'YYYY-MM-DD' desplazada N días desde hoy. */
export const ymdLocal = (desplazamientoDias = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + desplazamientoDias);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

export const DIAS_SEMANA: Array<{ valor: number; corto: string; largo: string }> = [
  { valor: 1, corto: "Lun", largo: "Lunes" },
  { valor: 2, corto: "Mar", largo: "Martes" },
  { valor: 3, corto: "Mié", largo: "Miércoles" },
  { valor: 4, corto: "Jue", largo: "Jueves" },
  { valor: 5, corto: "Vie", largo: "Viernes" },
  { valor: 6, corto: "Sáb", largo: "Sábado" },
  { valor: 7, corto: "Dom", largo: "Domingo" },
];

/** 'HH:MM' -> minutos desde la medianoche. */
export const aMinutos = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + (m || 0);
};

/** Turnos que entran en un tramo según la duración y la pausa. */
export const turnosEnTramo = (desde: string, hasta: string, duracion: number, pausa: number) => {
  const largo = aMinutos(hasta) - aMinutos(desde);
  if (!(duracion > 0) || largo < duracion) return 0;
  return Math.floor((largo - duracion) / (duracion + Math.max(pausa, 0))) + 1;
};

/** Resumen legible del horario semanal: 'Lun a Vie 08:00–12:00 y 14:00–17:00 · Sáb 08:00–12:00'.
 *  Agrupa los días consecutivos que tienen exactamente los mismos tramos. */
export const resumenHorario = (franjas: Array<{ dia_semana: number; hora_desde: string; hora_hasta: string }>) => {
  const porDia = new Map<number, string>();
  DIAS_SEMANA.forEach((d) => {
    const tramos = franjas
      .filter((f) => f.dia_semana === d.valor)
      .sort((a, b) => a.hora_desde.localeCompare(b.hora_desde))
      .map((f) => `${f.hora_desde}–${f.hora_hasta}`);
    if (tramos.length) porDia.set(d.valor, tramos.join(" y "));
  });
  const grupos: Array<{ desde: number; hasta: number; tramos: string }> = [];
  porDia.forEach((tramos, dia) => {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.tramos === tramos && ultimo.hasta === dia - 1) ultimo.hasta = dia;
    else grupos.push({ desde: dia, hasta: dia, tramos });
  });
  return grupos
    .map((g) => {
      const d1 = DIAS_SEMANA[g.desde - 1].corto;
      const dias = g.desde === g.hasta ? d1 : `${d1} ${g.hasta - g.desde === 1 ? "y" : "a"} ${DIAS_SEMANA[g.hasta - 1].corto}`;
      return `${dias} ${g.tramos}`;
    })
    .join(" · ");
};

export const CATEGORIAS: Record<Categoria, { etiqueta: string; color: string }> = {
  SALUD: { etiqueta: "Salud", color: "#0f766e" },
  BIENESTAR: { etiqueta: "Bienestar", color: "#7c3aed" },
  RRHH: { etiqueta: "RRHH", color: "#1d4ed8" },
  CAPACITACION: { etiqueta: "Capacitación", color: "#b45309" },
  BENEFICIOS: { etiqueta: "Beneficios", color: "#be185d" },
  OTRO: { etiqueta: "Otro", color: "#475569" },
};

/** Color de la card: el elegido o el de la categoría. */
/** Color de acento de la tarjeta: sale de la categoría de la agenda. */
export const colorCategoria = (categoria?: Categoria | null) =>
  (CATEGORIAS[categoria || "OTRO"] || CATEGORIAS.OTRO).color;

export const ETIQUETA_MODALIDAD: Record<ModalidadAgenda, string> = {
  PRESENCIAL: "Presencial",
  VIRTUAL: "Virtual",
  HIBRIDA: "Híbrida",
};

export const ETIQUETA_PUBLICO: Record<TipoPublico, string> = {
  GLOBAL: "Todos los funcionarios",
  ADMINISTRATIVOS: "Solo administrativos",
  OPERARIOS: "Solo operarios",
  LISTADO: "Listado de personas",
};

export const ESTADO_TEMA: Record<EstadoTema, { etiqueta: string; color: string }> = {
  BORRADOR: { etiqueta: "Borrador", color: "tertiary" },
  PUBLICADO: { etiqueta: "Publicada", color: "success" },
  PAUSADO: { etiqueta: "Pausada", color: "warning" },
  FINALIZADO: { etiqueta: "Finalizada", color: "medium" },
};

export const ESTADO_RESERVA: Record<EstadoReserva, { etiqueta: string; color: string }> = {
  CONFIRMADA: { etiqueta: "Confirmada", color: "success" },
  PENDIENTE: { etiqueta: "Pendiente de aprobación", color: "warning" },
  ESPERA: { etiqueta: "Lista de espera", color: "tertiary" },
  RECHAZADA: { etiqueta: "Rechazada", color: "danger" },
  CANCELADA: { etiqueta: "Cancelada", color: "medium" },
};

/** Agrupa una lista por fecha 'YYYY-MM-DD' manteniendo el orden. */
export function agruparPorDia<T>(items: T[], fecha: (item: T) => string): Array<[string, T[]]> {
  const mapa = new Map<string, T[]>();
  items.forEach((it) => {
    const dia = fecha(it).slice(0, 10);
    if (!mapa.has(dia)) mapa.set(dia, []);
    mapa.get(dia)!.push(it);
  });
  return Array.from(mapa.entries());
}

/** Cita confirmada -> evento para el calendario personal. El enlace de una cita
 *  virtual no va en el evento: la app lo muestra recién unos minutos antes. */
export function eventoDeCita(r: MiReserva): EventoCalendario {
  const lugar =
    r.modalidad === "VIRTUAL"
      ? "Videollamada (enlace en la app)"
      : r.modalidad === "HIBRIDA"
      ? `${r.lugar || ""} o videollamada (enlace en la app)`.trim()
      : r.lugar;
  const descripcion = [
    r.responsable_nombre ? `Con ${r.responsable_nombre}.` : "",
    `Modalidad: ${ETIQUETA_MODALIDAD[r.modalidad]}.`,
    r.modalidad !== "PRESENCIAL"
      ? "El enlace para conectarte aparece en la app El Mejor (Agendamientos > Mis citas) antes de la cita."
      : "",
    "Si no podés asistir, cancelá o cambiá el horario desde la app El Mejor (Agendamientos > Mis citas).",
  ]
    .filter(Boolean)
    .join("\n");
  return { uid: `agenda-reserva-${r.id}@elmejor`, titulo: r.titulo, inicio: r.fecha_inicio, fin: r.fecha_fin, lugar, descripcion };
}

/* ------------------------------------------------------------------
 * Calendario del gestor: las citas de la agenda para hacerles seguimiento
 * ------------------------------------------------------------------ */

/** Solo las citas que ocupan el turno (las canceladas, rechazadas o en espera no). */
const OCUPAN_TURNO: EstadoReserva[] = ["CONFIRMADA", "PENDIENTE"];

const claveTurno = (r: ReservaGestion) => `${r.responsable_id ?? "-"}|${r.fecha_inicio}`;

const lugarGestion = (tema: TemaDetalle) =>
  tema.modalidad === "VIRTUAL"
    ? `Videollamada${tema.link_virtual ? `: ${tema.link_virtual}` : ""}`
    : tema.modalidad === "HIBRIDA"
    ? `${tema.lugar || ""} o videollamada`.trim()
    : tema.lugar;

const personaDetalle = (r: ReservaGestion, confidencial: boolean) => {
  const datos = [r.nombre || "Sin nombre", `C.I. ${r.cedula}`, r.cargo || ""].filter(Boolean).join(" · ");
  const estado = r.estado === "PENDIENTE" ? " (pendiente de aprobación)" : "";
  // Mismo criterio que el Excel: en agendas confidenciales las respuestas no salen del sistema.
  const respuestas =
    !confidencial && r.respuestas?.length ? "\n" + r.respuestas.map((a) => `  ${a.pregunta}: ${a.valor}`).join("\n") : "";
  return `${datos}${estado}${respuestas}`;
};

/** Evento de un turno: en agendas individuales, la cita de una persona; en
 *  grupales, la sesión con la lista de anotados. */
function eventoDeTurno(tema: TemaDetalle, delTurno: ReservaGestion[]): EventoCalendario {
  const primera = delTurno[0];
  const grupal = tema.tipo_agenda === "GRUPAL";
  const pendientes = delTurno.filter((r) => r.estado === "PENDIENTE").length;
  const titulo = grupal
    ? `${tema.titulo} (${delTurno.length}/${tema.cupo_por_turno} anotados)`
    : `${tema.titulo}: ${primera.nombre || `C.I. ${primera.cedula}`}${pendientes ? " (pendiente)" : ""}`;
  const descripcion = [
    primera.responsable_nombre ? `Atiende: ${primera.responsable_nombre}` : "",
    `Modalidad: ${ETIQUETA_MODALIDAD[tema.modalidad]}`,
    tema.modalidad !== "VIRTUAL" && tema.modalidad !== "HIBRIDA" ? "" : tema.link_virtual ? `Enlace: ${tema.link_virtual}` : "",
    grupal ? `Anotados (${delTurno.length}):` : "Persona:",
    ...delTurno.map((r) => (grupal ? "- " : "") + personaDetalle(r, tema.confidencial)),
    "Detalle y acciones: app El Mejor > Gestión de agendas.",
  ]
    .filter(Boolean)
    .join("\n");
  const uid = grupal
    ? `agenda-sesion-${tema.id}-${primera.responsable_id ?? 0}-${primera.fecha_inicio.replace(/\D/g, "")}@elmejor`
    : `agenda-gestion-${primera.id}@elmejor`;
  return { uid, titulo, inicio: primera.fecha_inicio, fin: primera.fecha_fin, lugar: lugarGestion(tema), descripcion };
}

/** Todas las citas que ocupan turno -> eventos (agrupadas por sesión si la agenda es grupal). */
export function eventosDeGestion(tema: TemaDetalle, reservas: ReservaGestion[]): EventoCalendario[] {
  const activas = reservas.filter((r) => OCUPAN_TURNO.includes(r.estado));
  if (tema.tipo_agenda !== "GRUPAL") return activas.map((r) => eventoDeTurno(tema, [r]));
  const porTurno = new Map<string, ReservaGestion[]>();
  activas.forEach((r) => {
    const k = claveTurno(r);
    if (!porTurno.has(k)) porTurno.set(k, []);
    porTurno.get(k)!.push(r);
  });
  return Array.from(porTurno.values()).map((lista) => eventoDeTurno(tema, lista));
}

/** Evento de la cita (o de su sesión, si es grupal) para el botón de Google Calendar. */
export function eventoDeReservaGestion(tema: TemaDetalle, r: ReservaGestion, reservas: ReservaGestion[]): EventoCalendario {
  if (tema.tipo_agenda !== "GRUPAL") return eventoDeTurno(tema, [r]);
  const delTurno = reservas.filter((x) => OCUPAN_TURNO.includes(x.estado) && claveTurno(x) === claveTurno(r));
  return eventoDeTurno(tema, delTurno.length ? delTurno : [r]);
}
